import { ReactNode, useMemo } from "react"

import { ESCALATING_AI_ACTIONS } from "@domain/ai_eval_mocks"
import { AskQuestionEvalCaseResult } from "@domain/ai_evals"
import CancelIcon from "@mui/icons-material/Cancel"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CloseIcon from "@mui/icons-material/Close"
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import ScheduleIcon from "@mui/icons-material/Schedule"
import {
  Alert,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha
} from "@mui/material"
import { JsonAccordion } from "@pages/main/ai_answers/sub_components/AiStepsDrawer"

type StepDetail = {
  step?: string
  status?: string
  durationMs?: number
  source?: string
}

type ActualShape = {
  status?: string
  content?: string
  aiAction?: string
  steps?: string[]
  stepDetails?: StepDetail[]
  turns?: {
    query?: string
    content?: string
    aiAction?: string
    steps?: string[]
    failures?: string[]
  }[]
}

type ExpectedShape = {
  expectedStatus?: string
  expectedAiAction?: string
  expectedContentIncludes?: string[]
  requiredSteps?: string[]
  forbiddenSteps?: string[]
}

/**
 * Mirrors evaluateAskQuestionEvalAssertions in api-v2
 * (QpienAi/services/askQuestionEval.service.ts): case-insensitive substring for content,
 * exact string equality for steps/action/status. Keep in sync or the UI will disagree
 * with the stored verdict.
 */
const contentMatches = (content: string, needle: string) =>
  content.toLowerCase().includes(needle.toLowerCase())

type CheckRow = {
  key: string
  label: string
  hint?: string
  state: "passed" | "failed" | "skipped"
  expected: ReactNode
  actual: ReactNode
}

const MutedDash = () => (
  <Typography variant="caption" color="text.disabled">
    —
  </Typography>
)

function Mono({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      fontFamily="monospace"
      sx={{ fontSize: 12, wordBreak: "break-word" }}
    >
      {children}
    </Typography>
  )
}

function StateIcon({ state }: { state: CheckRow["state"] }) {
  if (state === "passed") {
    return <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
  }
  if (state === "failed") {
    return <CancelIcon sx={{ fontSize: 18, color: "error.main" }} />
  }
  return (
    <Tooltip title="Bu kontrol tanımlanmadı, değerlendirilmedi">
      <RemoveCircleOutlineIcon sx={{ fontSize: 18, color: "text.disabled" }} />
    </Tooltip>
  )
}

export function CaseComparisonDrawer({
  selectedCase,
  actual,
  stepsLoading,
  onClose
}: {
  selectedCase: AskQuestionEvalCaseResult | null
  actual: Record<string, unknown>
  stepsLoading?: boolean
  onClose: () => void
}) {
  // Memoized together so the `|| {}` fallbacks don't produce fresh objects every render and
  // invalidate the checks memo below.
  const { act, exp, answer, actualSteps } = useMemo(() => {
    const nextAct = (actual || {}) as ActualShape
    const nextExp = (selectedCase?.expected || {}) as ExpectedShape
    return {
      act: nextAct,
      exp: nextExp,
      answer: nextAct.content || selectedCase?.answer || "",
      actualSteps: nextAct.steps || []
    }
  }, [actual, selectedCase])

  const checks = useMemo<CheckRow[]>(() => {
    const rows: CheckRow[] = []

    // Status
    rows.push({
      key: "status",
      label: "Durum",
      state: !exp.expectedStatus
        ? "skipped"
        : act.status === exp.expectedStatus
          ? "passed"
          : "failed",
      expected: exp.expectedStatus ? (
        <Mono>{exp.expectedStatus}</Mono>
      ) : (
        <MutedDash />
      ),
      actual: act.status ? <Mono>{act.status}</Mono> : <MutedDash />
    })

    // aiAction — 'positive' means "answered instead of escalating", see evaluateAiAction()
    const wantsAnswer = exp.expectedAiAction === "positive"
    const escalated = (ESCALATING_AI_ACTIONS as string[]).includes(
      act.aiAction || ""
    )
    const actionPassed = wantsAnswer
      ? !escalated && Boolean(answer.trim())
      : act.aiAction === exp.expectedAiAction
    const actionState = !exp.expectedAiAction
      ? "skipped"
      : actionPassed
        ? "passed"
        : "failed"
    rows.push({
      key: "aiAction",
      label: wantsAnswer ? "Cevapladı mı" : "AI Aksiyonu",
      hint: wantsAnswer
        ? "Eskalasyona gitmediyse geçer — chat RAG başarıda aiAction'ı boş bırakır"
        : undefined,
      state: actionState,
      expected: exp.expectedAiAction ? (
        <Chip
          size="small"
          label={
            wantsAnswer
              ? "cevap üretildi (eskalasyon yok)"
              : exp.expectedAiAction
          }
          variant="outlined"
          sx={{ fontSize: 11, fontWeight: 600 }}
        />
      ) : (
        <MutedDash />
      ),
      actual: (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
        >
          <Chip
            size="small"
            label={act.aiAction || "undefined"}
            color={actionState === "passed" ? "success" : "error"}
            variant="outlined"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              fontStyle: act.aiAction ? "normal" : "italic"
            }}
          />
          {wantsAnswer && !act.aiAction && !escalated && (
            <Typography variant="caption" color="text.secondary">
              (normal — chat yolu yazmaz)
            </Typography>
          )}
        </Stack>
      )
    })

    // Content includes — ANY match
    const needles = exp.expectedContentIncludes || []
    const matchedNeedles = needles.filter((n) => contentMatches(answer, n))
    rows.push({
      key: "content",
      label: needles.length
        ? `İçerik (${matchedNeedles.length}/${needles.length})`
        : "İçerik",
      hint: needles.length
        ? "Herhangi biri eşleşirse yeterli (ANY match)"
        : undefined,
      state: !needles.length
        ? "skipped"
        : matchedNeedles.length > 0
          ? "passed"
          : "failed",
      expected: needles.length ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {needles.map((n) => {
            const hit = contentMatches(answer, n)
            return (
              <Tooltip
                key={n}
                title={hit ? "Cevapta bulundu" : "Cevapta bulunamadı"}
              >
                <Chip
                  size="small"
                  label={n}
                  color={hit ? "success" : "default"}
                  variant={hit ? "filled" : "outlined"}
                  sx={{
                    fontSize: 11,
                    opacity: hit ? 1 : 0.55,
                    textDecoration: hit ? "none" : "line-through"
                  }}
                />
              </Tooltip>
            )
          })}
        </Stack>
      ) : (
        <MutedDash />
      ),
      actual: needles.length ? (
        <Typography variant="caption" color="text.secondary">
          {matchedNeedles.length > 0
            ? `Eşleşen: ${matchedNeedles.join(", ")}`
            : "Hiçbiri cevapta yok"}
        </Typography>
      ) : (
        <MutedDash />
      )
    })

    // Required steps
    const required = exp.requiredSteps || []
    const missing = required.filter((s) => !actualSteps.includes(s))
    rows.push({
      key: "requiredSteps",
      label: required.length
        ? `Zorunlu adımlar (${required.length - missing.length}/${required.length})`
        : "Zorunlu adımlar",
      hint: required.length ? "Hepsi çalışmış olmalı (ALL)" : undefined,
      state: !required.length
        ? "skipped"
        : missing.length
          ? "failed"
          : "passed",
      expected: required.length ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {required.map((s) => {
            const ran = actualSteps.includes(s)
            return (
              <Chip
                key={s}
                size="small"
                label={s}
                color={ran ? "success" : "error"}
                variant={ran ? "filled" : "outlined"}
                sx={{ fontSize: 10, fontFamily: "monospace" }}
              />
            )
          })}
        </Stack>
      ) : (
        <MutedDash />
      ),
      actual: required.length ? (
        <Typography variant="caption" color="text.secondary">
          {missing.length
            ? `Çalışmayan: ${missing.join(", ")}`
            : "Tümü çalıştı"}
        </Typography>
      ) : (
        <MutedDash />
      )
    })

    // Forbidden steps
    const forbidden = exp.forbiddenSteps || []
    const violated = forbidden.filter((s) => actualSteps.includes(s))
    rows.push({
      key: "forbiddenSteps",
      label: "Yasak adımlar",
      hint: forbidden.length ? "Hiçbiri çalışmamalı" : undefined,
      state: !forbidden.length
        ? "skipped"
        : violated.length
          ? "failed"
          : "passed",
      expected: forbidden.length ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {forbidden.map((s) => {
            const ran = actualSteps.includes(s)
            return (
              <Chip
                key={s}
                size="small"
                label={s}
                color={ran ? "error" : "default"}
                variant={ran ? "filled" : "outlined"}
                sx={{ fontSize: 10, fontFamily: "monospace" }}
              />
            )
          })}
        </Stack>
      ) : (
        <MutedDash />
      ),
      actual: forbidden.length ? (
        <Typography variant="caption" color="text.secondary">
          {violated.length ? `İhlal: ${violated.join(", ")}` : "İhlal yok"}
        </Typography>
      ) : (
        <MutedDash />
      )
    })

    return rows
  }, [exp, act, answer, actualSteps])

  const evaluated = checks.filter((c) => c.state !== "skipped")
  const passedCount = evaluated.filter((c) => c.state === "passed").length
  const failures = selectedCase?.failures || []
  const turns = act.turns || []
  const stepDetails = act.stepDetails || []

  return (
    <Drawer anchor="right" open={Boolean(selectedCase)} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "100vw", sm: "92vw", lg: "min(1180px, 90vw)" },
          display: "flex",
          flexDirection: "column",
          height: "100%"
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Chip
            size="small"
            label={selectedCase?.status === "PASSED" ? "Geçti" : "Başarısız"}
            color={selectedCase?.status === "PASSED" ? "success" : "error"}
            sx={{ fontWeight: 700 }}
          />
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            {selectedCase?.title}
          </Typography>
          {evaluated.length > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={`${passedCount}/${evaluated.length} kontrol`}
              color={passedCount === evaluated.length ? "success" : "warning"}
              sx={{ fontWeight: 600 }}
            />
          )}
          {typeof selectedCase?.durationMs === "number" && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <ScheduleIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              <Typography variant="caption" color="text.secondary">
                {(selectedCase.durationMs / 1000).toFixed(2)}s
              </Typography>
            </Stack>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Stack spacing={3}>
            {selectedCase?.error && (
              <Alert severity="error">
                <strong>Çalıştırma hatası:</strong> {selectedCase.error}
              </Alert>
            )}

            {/* Soru + Cevap */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="stretch"
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SectionLabel>Müşteri sorusu</SectionLabel>
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "action.hover",
                    height: "100%"
                  }}
                >
                  <Typography variant="body2">
                    {selectedCase?.question || "—"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1.4, minWidth: 0 }}>
                <SectionLabel>{"AI'ın ürettiği cevap"}</SectionLabel>
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor:
                      selectedCase?.status === "PASSED"
                        ? "success.light"
                        : "error.light",
                    maxHeight: 260,
                    overflow: "auto"
                  }}
                >
                  {answer ? (
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                    >
                      {answer}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      fontStyle="italic"
                    >
                      Cevap üretilmedi (boş içerik)
                    </Typography>
                  )}
                </Box>
              </Box>
            </Stack>

            {/* Karşılaştırma tablosu */}
            <Box>
              <SectionLabel>Doğrulama karşılaştırması</SectionLabel>
              <Box
                sx={{
                  mt: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden"
                }}
              >
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ width: 44 }} />
                      <TableCell sx={{ width: 190, fontWeight: 700 }}>
                        Kontrol
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Beklenen</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Gerçekleşen
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {checks.map((row) => (
                      <TableRow
                        key={row.key}
                        sx={{
                          opacity: row.state === "skipped" ? 0.6 : 1,
                          bgcolor: (theme) =>
                            row.state === "failed"
                              ? alpha(theme.palette.error.main, 0.06)
                              : "transparent",
                          "& td": { verticalAlign: "top", py: 1.25 }
                        }}
                      >
                        <TableCell>
                          <StateIcon state={row.state} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontWeight={600}>
                            {row.label}
                          </Typography>
                          {row.hint && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{
                                display: "block",
                                fontSize: 10.5,
                                mt: 0.25
                              }}
                            >
                              {row.hint}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{row.expected}</TableCell>
                        <TableCell>{row.actual}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              {evaluated.length === 0 && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Bu case için hiçbir assertion tanımlanmamış — sadece çalıştı,
                  hiçbir şey doğrulanmadı.
                </Alert>
              )}
            </Box>

            {/* Motorun raporladığı hatalar */}
            {failures.length > 0 && (
              <Box>
                <SectionLabel>Motorun raporladığı hatalar</SectionLabel>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {failures.map((failure, i) => (
                    <Alert
                      key={`${failure}-${i}`}
                      severity="error"
                      sx={{
                        py: 0.25,
                        "& .MuiAlert-message": { fontSize: 12.5 }
                      }}
                    >
                      {failure}
                    </Alert>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Adım akışı */}
            <Box>
              <SectionLabel>
                Çalışan adım akışı{" "}
                {actualSteps.length > 0 && `(${actualSteps.length})`}
              </SectionLabel>
              <Box sx={{ mt: 1 }}>
                {stepsLoading && !actualSteps.length ? (
                  <Typography variant="caption" color="text.secondary">
                    Adımlar yükleniyor...
                  </Typography>
                ) : actualSteps.length === 0 ? (
                  <Typography variant="caption" color="text.disabled">
                    Adım kaydı yok
                  </Typography>
                ) : (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center"
                  >
                    {actualSteps.map((step, i) => {
                      const detail = stepDetails.find((d) => d.step === step)
                      const isRequired = (exp.requiredSteps || []).includes(
                        step
                      )
                      const isForbidden = (exp.forbiddenSteps || []).includes(
                        step
                      )
                      const failed = detail?.status === "FAILED"
                      const label = detail?.durationMs
                        ? `${step} · ${detail.durationMs}ms`
                        : step
                      return (
                        <Stack
                          key={`${step}-${i}`}
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          {i > 0 && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ mx: 0.25 }}
                            >
                              →
                            </Typography>
                          )}
                          <Tooltip
                            title={
                              isForbidden
                                ? "Bu adım yasaklıydı ama çalıştı"
                                : isRequired
                                  ? "Zorunlu adım — çalıştı"
                                  : detail?.status || step
                            }
                          >
                            <Chip
                              size="small"
                              label={label}
                              color={
                                isForbidden || failed
                                  ? "error"
                                  : isRequired
                                    ? "success"
                                    : "default"
                              }
                              variant={
                                isForbidden || isRequired || failed
                                  ? "filled"
                                  : "outlined"
                              }
                              sx={{ fontSize: 10, fontFamily: "monospace" }}
                            />
                          </Tooltip>
                        </Stack>
                      )
                    })}
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Çok turlu senaryo dökümü */}
            {turns.length > 1 && (
              <Box>
                <SectionLabel>Turn bazlı döküm ({turns.length})</SectionLabel>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {turns.map((turn, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: turn.failures?.length
                          ? "error.light"
                          : "divider",
                        borderRadius: 2
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 0.75 }}
                      >
                        <Chip
                          size="small"
                          label={`Turn ${i + 1}`}
                          color={turn.failures?.length ? "error" : "success"}
                          sx={{ fontSize: 10, fontWeight: 700 }}
                        />
                        {turn.aiAction && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={turn.aiAction}
                            sx={{ fontSize: 10 }}
                          />
                        )}
                      </Stack>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", fontWeight: 600 }}
                      >
                        {turn.query}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {turn.content || "—"}
                      </Typography>
                      {(turn.steps || []).length > 0 && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ mt: 0.75 }}
                        >
                          {(turn.steps || []).map((s, si) => (
                            <Chip
                              key={`${s}-${si}`}
                              size="small"
                              label={s}
                              variant="outlined"
                              sx={{ fontSize: 9.5, fontFamily: "monospace" }}
                            />
                          ))}
                        </Stack>
                      )}
                      {(turn.failures || []).map((f, fi) => (
                        <Typography
                          key={fi}
                          variant="caption"
                          color="error.main"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          • {f}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />

            {/* Ham veri — derin debug için */}
            <Box>
              <SectionLabel>Ham veri</SectionLabel>
              <Stack spacing={1} sx={{ mt: 1 }}>
                <JsonAccordion title="Gerçekleşen (actual)" data={actual} />
                <JsonAccordion
                  title="Beklenen (expected)"
                  data={selectedCase?.expected || {}}
                />
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 0.8, fontSize: 10.5 }}
    >
      {children}
    </Typography>
  )
}
